import { CandidateProfile } from "../../../Candidate/Types/CandidateType";
import { PaginationResponse } from "../../../Shared/Pagination/PaginationResponse";

/**
 * Single source of truth for the Recruiter Intelligence workspace.
 * Dashboard segments, the advanced filter panel, the query builder and the
 * search all operate on this exact same object - one shared search engine.
 */
export interface TalentFilters {
  keyword: string;
  totalExperienceMin?: number;
  totalExperienceMax?: number;
  leadershipExperienceMin?: number;
  roles: string[];
  roleLevels: string[];
  managementLevels: string[];
  teamSizeMin?: number;
  leadershipResponsibilities: string[];
  impactAreas: string[];
  achievementSignals: string[];
  skills: string[];
  skillsWithYears: { skill: string; years: number }[];
  industries: string[];
  companies: string[];
  companyTypes: string[];
  cities: string[];
  availabilityShifts: string[];
  maxNoticeDays?: number;
  minNoticeDays?: number;
  workModes: string[];
  relocations: string[];
  workPreferences: string[];
  nexgentSignals: string[];
  leadershipScoreMin?: number;
  overallScoreMin?: number;
  profileCompleteScoreMin?: number;
  createdSinceDays?: number;
  updatedSinceDays?: number;
}

export const initialTalentFilters: TalentFilters = {
  keyword: "",
  roles: [],
  roleLevels: [],
  managementLevels: [],
  leadershipResponsibilities: [],
  impactAreas: [],
  achievementSignals: [],
  skills: [],
  skillsWithYears: [],
  industries: [],
  companies: [],
  companyTypes: [],
  cities: [],
  availabilityShifts: [],
  workModes: [],
  relocations: [],
  workPreferences: [],
  nexgentSignals: [],
};

/** Serializes the filter object into the payload understood by the backend. */
export const serializeTalentFilters = (filters: TalentFilters): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  if (filters.keyword?.trim()) out.keyword = filters.keyword.trim();
  if (filters.totalExperienceMin !== undefined && filters.totalExperienceMin !== null) out.totalExperienceMin = filters.totalExperienceMin;
  if (filters.totalExperienceMax !== undefined && filters.totalExperienceMax !== null) out.totalExperienceMax = filters.totalExperienceMax;
  if (filters.leadershipExperienceMin !== undefined && filters.leadershipExperienceMin !== null) out.leadershipExperienceMin = filters.leadershipExperienceMin;
  if (filters.teamSizeMin !== undefined && filters.teamSizeMin !== null) out.teamSizeMin = filters.teamSizeMin;
  if (filters.maxNoticeDays !== undefined && filters.maxNoticeDays !== null) out.maxNoticeDays = filters.maxNoticeDays;
  if (filters.minNoticeDays !== undefined && filters.minNoticeDays !== null) out.minNoticeDays = filters.minNoticeDays;
  if (filters.leadershipScoreMin !== undefined && filters.leadershipScoreMin !== null) out.leadershipScoreMin = filters.leadershipScoreMin;
  if (filters.overallScoreMin !== undefined && filters.overallScoreMin !== null) out.overallScoreMin = filters.overallScoreMin;
  if (filters.profileCompleteScoreMin !== undefined && filters.profileCompleteScoreMin !== null) out.profileCompleteScoreMin = filters.profileCompleteScoreMin;
  if (filters.createdSinceDays !== undefined && filters.createdSinceDays !== null) out.createdSinceDays = filters.createdSinceDays;
  if (filters.updatedSinceDays !== undefined && filters.updatedSinceDays !== null) out.updatedSinceDays = filters.updatedSinceDays;

  const listKeys = [
    "roles", "roleLevels", "managementLevels", "leadershipResponsibilities",
    "impactAreas", "achievementSignals", "skills", "industries", "companies",
    "companyTypes", "cities", "availabilityShifts", "workModes", "relocations",
    "workPreferences", "nexgentSignals",
  ] as const;
  for (const key of listKeys) {
    const value = filters[key] as string[] | undefined;
    if (value && value.length) out[key] = value;
  }
  if (filters.skillsWithYears && filters.skillsWithYears.length) out.skillsWithYears = filters.skillsWithYears;

  return out;
};

export const talentFiltersActiveCount = (filters: TalentFilters): number => {
  let count = 0;
  if (filters.keyword?.trim()) count += 1;
  if (filters.totalExperienceMin !== undefined || filters.totalExperienceMax !== undefined) count += 1;
  if (filters.leadershipExperienceMin !== undefined) count += 1;
  if (filters.teamSizeMin !== undefined) count += 1;
  if (filters.maxNoticeDays !== undefined || filters.minNoticeDays !== undefined) count += 1;
  if (filters.leadershipScoreMin !== undefined || filters.overallScoreMin !== undefined || filters.profileCompleteScoreMin !== undefined) count += 1;
  if (filters.createdSinceDays !== undefined || filters.updatedSinceDays !== undefined) count += 1;
  const listKeys = [
    "roles", "roleLevels", "managementLevels", "leadershipResponsibilities",
    "impactAreas", "achievementSignals", "skills", "industries", "companies",
    "companyTypes", "cities", "availabilityShifts", "workModes", "relocations",
    "workPreferences", "nexgentSignals",
  ] as const;
  for (const key of listKeys) {
    count += (filters[key] as string[]).length;
  }
  count += filters.skillsWithYears.length;
  return count;
};

/** A chip shown in the query builder; removing it re-generates the filters. */
export interface FilterChip {
  id: string;
  label: string;
  category: string;
  remove: () => Partial<TalentFilters>;
}

export interface TalentMetric {
  label: string;
  count: number;
  filters: Record<string, unknown>;
  subLabel?: string;
}

export interface TalentDistribution {
  type: "experience" | "seniority" | "location" | "industry" | "availability" | "skills" | "roles";
  items: TalentMetric[];
}

export interface TalentIntelligenceDashboard {
  totalTalent: TalentMetric;
  leadershipPool: TalentMetric;
  openToWork: TalentMetric;
  verified: TalentMetric;
  recentlyUpdated: TalentMetric;
  newThisWeek: TalentMetric;
  distributions: TalentDistribution[];
  generatedAtEpochMilli: number;
}

export type TalentSearchResponse = PaginationResponse<CandidateProfile>;

/** Decodes a raw backend filter payload (as returned by dashboard metrics)
 * into the frontend TalentFilters shape, so dashboard clicks and manual
 * filter selection share one identical search state. */
export const decodeRawFilter = (raw: Record<string, unknown>): Partial<TalentFilters> => {
  const patch: Partial<TalentFilters> = {};

  if (typeof raw.keyword === "string") patch.keyword = raw.keyword;

  const numKeys = [
    "totalExperienceMin", "totalExperienceMax", "leadershipExperienceMin",
    "teamSizeMin", "maxNoticeDays", "minNoticeDays", "leadershipScoreMin",
    "overallScoreMin", "profileCompleteScoreMin", "createdSinceDays", "updatedSinceDays",
  ] as const;
  for (const key of numKeys) {
    const value = raw[key];
    if (typeof value === "number") (patch as Record<string, unknown>)[key] = value;
  }

  const listKeys = [
    "roles", "roleLevels", "managementLevels", "leadershipResponsibilities",
    "impactAreas", "achievementSignals", "skills", "industries", "companies",
    "companyTypes", "cities", "availabilityShifts", "workModes", "relocations",
    "workPreferences", "nexgentSignals",
  ] as const;
  for (const key of listKeys) {
    const value = raw[key];
    if (Array.isArray(value) && value.length) (patch as Record<string, unknown>)[key] = value as string[];
  }

  if (Array.isArray(raw.skillsWithYears)) {
    patch.skillsWithYears = raw.skillsWithYears as { skill: string; years: number }[];
  }

  return patch;
};

/** Builds removable query-builder chips from the shared filter state. */
export const buildChips = (filters: TalentFilters): FilterChip[] => {
  const chips: FilterChip[] = [];
  const push = (id: string, label: string, category: string, remove: () => Partial<TalentFilters>) =>
    chips.push({ id: `${category}:${id}:${label}`, label, category, remove });

  if (filters.keyword?.trim()) push("keyword", filters.keyword.trim(), "Keyword", () => ({ keyword: "" }));
  if (filters.totalExperienceMin !== undefined) push("expMin", `${filters.totalExperienceMin}+ yrs`, "Experience", () => ({ totalExperienceMin: undefined }));
  if (filters.totalExperienceMax !== undefined) push("expMax", `≤ ${filters.totalExperienceMax} yrs`, "Experience", () => ({ totalExperienceMax: undefined }));
  if (filters.leadershipExperienceMin !== undefined) push("leadExp", `${filters.leadershipExperienceMin}+ yrs leadership`, "Leadership", () => ({ leadershipExperienceMin: undefined }));
  if (filters.teamSizeMin !== undefined) push("team", `${filters.teamSizeMin}+ team`, "Team Size", () => ({ teamSizeMin: undefined }));
  if (filters.maxNoticeDays !== undefined) push("notice", `Avail ≤ ${filters.maxNoticeDays}d`, "Availability", () => ({ maxNoticeDays: undefined }));
  if (filters.minNoticeDays !== undefined) push("noticeMin", `Notice > ${filters.minNoticeDays}d`, "Availability", () => ({ minNoticeDays: undefined }));
  if (filters.leadershipScoreMin !== undefined) push("leadScore", `Ldrship ≥ ${filters.leadershipScoreMin}`, "Score", () => ({ leadershipScoreMin: undefined }));
  if (filters.overallScoreMin !== undefined) push("overallScore", `Overall ≥ ${filters.overallScoreMin}`, "Score", () => ({ overallScoreMin: undefined }));
  if (filters.profileCompleteScoreMin !== undefined) push("pcs", `Profile ≥ ${filters.profileCompleteScoreMin}`, "Score", () => ({ profileCompleteScoreMin: undefined }));
  if (filters.createdSinceDays !== undefined) push("created", `New (${filters.createdSinceDays}d)`, "Recency", () => ({ createdSinceDays: undefined }));
  if (filters.updatedSinceDays !== undefined) push("updated", `Updated (${filters.updatedSinceDays}d)`, "Recency", () => ({ updatedSinceDays: undefined }));

  const listMap: { key: keyof TalentFilters; category: string }[] = [
    { key: "roles", category: "Role" },
    { key: "roleLevels", category: "Role" },
    { key: "managementLevels", category: "Leadership" },
    { key: "leadershipResponsibilities", category: "Leadership" },
    { key: "impactAreas", category: "Business Impact" },
    { key: "achievementSignals", category: "Business Impact" },
    { key: "skills", category: "Skills" },
    { key: "industries", category: "Industry" },
    { key: "companies", category: "Company" },
    { key: "companyTypes", category: "Company" },
    { key: "cities", category: "Location" },
    { key: "availabilityShifts", category: "Availability" },
    { key: "workModes", category: "Work Mode" },
    { key: "relocations", category: "Work Mode" },
    { key: "workPreferences", category: "Work Mode" },
    { key: "nexgentSignals", category: "Nexgent Signal" },
  ];

  for (const { key, category } of listMap) {
    const list = filters[key] as string[];
    for (const value of list) {
      push(String(key), value, category, () => ({
        [key]: (filters[key] as string[]).filter((v) => v !== value),
      }));
    }
  }

  for (const entry of filters.skillsWithYears) {
    push("skillYW", `${entry.skill} ≥ ${entry.years} yrs`, "Skills", () => ({
      skillsWithYears: filters.skillsWithYears.filter((s) => s.skill !== entry.skill),
    }));
  }

  return chips;
};

export const ROLE_OPTIONS = [
  "QA Lead", "QA Manager", "Senior QA Manager", "QA Director", "Engineering Manager",
  "Senior Engineering Manager", "Engineering Director", "VP Engineering", "VP QA",
  "CTO", "CEO", "Automation Architect", "Engineering Architect", "Other",
];

export const MANAGEMENT_LEVEL_OPTIONS = [
  "Individual Contributor", "Team Lead", "Manager", "Senior Manager", "Director",
  "Senior Director", "VP", "C-Level",
];

export const LEADERSHIP_RESPONSIBILITY_OPTIONS = [
  "Team building", "Hiring", "Performance management", "Engineering strategy",
  "QA strategy", "Delivery ownership", "Technical strategy", "Transformation leadership",
  "Cross-functional leadership", "Stakeholder management", "Executive reporting",
];

export const IMPACT_AREA_OPTIONS = [
  "Revenue impact", "Cost optimization", "Productivity improvement", "Quality improvement",
  "Automation", "Digital transformation", "Process transformation", "Product delivery",
  "Engineering transformation", "Customer experience", "Operational efficiency",
  "Platform scalability", "Reliability", "Security", "Compliance",
];

export const ACHIEVEMENT_SIGNAL_OPTIONS = [
  "Built a team", "Scaled a team", "Built a function", "Started a QA organization",
  "Started an engineering organization", "Reduced defects", "Reduced release time",
  "Improved automation", "Improved productivity", "Improved reliability",
  "Led transformation", "Delivered large-scale programs",
];

export const SKILL_OPTIONS: { label: string; group: string }[] = [
  { label: "Java", group: "Programming" },
  { label: "Python", group: "Programming" },
  { label: "JavaScript", group: "Programming" },
  { label: "TypeScript", group: "Programming" },
  { label: "C#", group: "Programming" },
  { label: "C++", group: "Programming" },
  { label: "Go", group: "Programming" },
  { label: "Selenium", group: "QA / Automation" },
  { label: "Cypress", group: "QA / Automation" },
  { label: "Playwright", group: "QA / Automation" },
  { label: "Appium", group: "QA / Automation" },
  { label: "API Testing", group: "QA / Automation" },
  { label: "Performance Testing", group: "QA / Automation" },
  { label: "Automation Frameworks", group: "QA / Automation" },
  { label: "CI/CD", group: "QA / Automation" },
  { label: "Test Architecture", group: "QA / Automation" },
  { label: "Quality Engineering", group: "QA / Automation" },
  { label: "Spring Boot", group: "Engineering" },
  { label: "React", group: "Engineering" },
  { label: "Node.js", group: "Engineering" },
  { label: "Microservices", group: "Engineering" },
  { label: "Cloud", group: "Engineering" },
  { label: "AWS", group: "Engineering" },
  { label: "Azure", group: "Engineering" },
  { label: "GCP", group: "Engineering" },
  { label: "Kubernetes", group: "Engineering" },
  { label: "Docker", group: "Engineering" },
  { label: "Distributed Systems", group: "Engineering" },
  { label: "System Design", group: "Engineering" },
];

export const INDUSTRY_OPTIONS = [
  "SaaS", "FinTech", "Banking", "Insurance", "Healthcare", "E-commerce", "Retail",
  "Manufacturing", "Telecom", "EdTech", "Logistics", "Travel", "Enterprise Software",
  "Consumer Technology", "AI / ML", "Cybersecurity", "Other",
];

export const COMPANY_TYPE_OPTIONS = [
  "Product", "Services", "Startup", "Enterprise", "Consulting", "Global MNC", "Indian company",
];

export const AVAILABILITY_SHIFT_OPTIONS = [
  "Open to opportunities", "Selectively exploring", "Not actively looking", "Not specified",
];

export const AVAILABILITY_NOTICE_OPTIONS = [
  "Immediately available", "15 days", "30 days", "60 days", "90 days", "Other",
];

export const WORK_MODE_OPTIONS = ["Remote", "Hybrid", "On-site"];

export const RELOCATION_OPTIONS = ["Open to relocation", "Not open", "Not specified"];

export const WORK_PREFERENCE_OPTIONS = ["Remote", "Hybrid", "Office"];

export const NEXGENT_SIGNAL_OPTIONS = [
  "Nexgent Verified", "Profile Complete", "Leadership Experience",
  "Open to Opportunities", "Platform Contributor", "Founding Member",
];

export const TEAM_SIZE_BUCKETS = [
  { label: "1-10", min: 1 },
  { label: "11-25", min: 11 },
  { label: "26-50", min: 26 },
  { label: "51-100", min: 51 },
  { label: "101-250", min: 101 },
  { label: "250+", min: 251 },
];

export const EXPERIENCE_BUCKETS = [
  { label: "10+", min: 10 },
  { label: "12+", min: 12 },
  { label: "15+", min: 15 },
  { label: "20+", min: 20 },
];

export const LEADERSHIP_EXPERIENCE_BUCKETS = [
  { label: "3+", min: 3 },
  { label: "5+", min: 5 },
  { label: "8+", min: 8 },
  { label: "10+", min: 10 },
];

export const DEFAULT_RESULT_COLUMNS = ["candidate", "role", "experience", "leadership", "team", "industry", "location", "signals", "match"];

export const OPTIONAL_RESULT_COLUMNS = [
  { key: "skills", label: "Skills" },
  { key: "currentCompany", label: "Current Company" },
  { key: "previousCompanies", label: "Previous Companies" },
  { key: "businessImpact", label: "Business Impact" },
  { key: "availability", label: "Availability" },
  { key: "opportunityStatus", label: "Opportunity Status" },
  { key: "responseBehavior", label: "Response Behaviour" },
  { key: "profileCompletion", label: "Profile Completion" },
  { key: "verification", label: "Verification" },
  { key: "lastActive", label: "Last Active" },
];